import Immutable from "immutable";
import exactMath from "exact-math";
import { WEEKDAYS } from "lib/date";

import { getBasketItemByID, doesBasketItemExist } from "state/school_money/selectors";

/**
 * @returns {Immutable.List}
 */
export function getClubBookingIDList(state) {
  return state.getIn(["school_money", "clubBookings"]).keySeq().toList();
}

/**
 * @returns {boolean}
 */
export function doesClubBookingExist(state, clubBookingID) {
  return getClubBookingIDList(state).includes(clubBookingID);
}

/**
 * @returns {Immutable.List}
 */
export function getStudentClubBookingIDList(state, studentID) {
  return state
    .getIn(["school_money", "clubBookings"])
    .filter(clubBooking => clubBooking.placesLimitReached)
    .filter(clubBooking => clubBooking.studentID === studentID)
    .sortBy(clubBooking => clubBooking.startDate)
    .keySeq().toList();
}

/**
 * @returns {Immutable.Map}
 */
export function getClubBookingByID(state, clubBookingID) {
  return state.getIn(["school_money", "clubBookings", clubBookingID]);
}

/**
 * @returns {Immutable.List}
 */
export function getClubBookingSessionList(state, clubBookingID) {
  const clubBooking = getClubBookingByID(state, clubBookingID);
  return clubBooking.get("sessions").valueSeq().toList()
    .map(session => Immutable.fromJS({
      id: session.get("id"),
      name: `${session.get("id")} - ${session.get("description")}`,
      price: session.get("price")
    }));
}

/**
 * @returns {string}
 */
export function getClubBookingBookedSessionID(state, clubBookingID, weekdayIndex) {
  const clubBooking = getClubBookingByID(state, clubBookingID);
  return clubBooking.getIn(["bookedSessionIDs", weekdayIndex]);
}

/**
 * @returns {boolean}
 */
export function isClubBookingDayBooked(state, clubBookingID, weekdayIndex) {
  const bookedSessionID = getClubBookingBookedSessionID(state, clubBookingID, weekdayIndex);
  return bookedSessionID !== null;
}

/**
 * @returns {Immutable.List}
 */
export function getClubBookingSelectedSessionIDList(state, clubBookingID) {
  const isInBasket = isClubBookingInBasket(state, clubBookingID);
  if (isInBasket) {
    const basketItem = getBasketItemByID(state, "clubBooking", clubBookingID);
    return basketItem.payload.get("sessionIDs");
  } else {
    const clubBooking = getClubBookingByID(state, clubBookingID);
    return clubBooking.selectedSessionIDs;
  }
}

/**
 * @returns {string}
 */
export function getClubBookingSelectedSessionID(state, clubBookingID, weekdayIndex) {
  const sessionIDList = getClubBookingSelectedSessionIDList(state, clubBookingID);
  return sessionIDList.get(weekdayIndex);
}

/**
 * @returns {Immutable.Map}
 */
export function getClubBookingSession(state, clubBookingID, sessionID) {
  const clubBooking = getClubBookingByID(state, clubBookingID);
  return clubBooking.getIn(["sessions", sessionID]);
}

/**
 * @returns {Immutable.Map}
 */
export function getClubBookingSelectedSession(state, clubBookingID, weekdayIndex) {
  const sessionID = getClubBookingSelectedSessionID(state, clubBookingID, weekdayIndex);
  return getClubBookingSession(state, clubBookingID, sessionID);
}

/**
 * @returns {number}
 */
export function getClubBookingTotalPrice(state, clubBookingID) {
  return WEEKDAYS
    .map((_, weekdayIndex) => getClubBookingSelectedSessionID(state, clubBookingID, weekdayIndex))
    .filter(sessionID => sessionID !== null)
    .map(sessionID => getClubBookingSession(state, clubBookingID, sessionID))
    .map(session => session.get("price"))
    .reduce((totalPrice, sessionPrice) => exactMath.add(totalPrice, sessionPrice), 0);
}

/**
 * Work out the price that needs to be paid towards this club booking.
 * Calculated as the difference between the total price of all selections,
 *   and the price of all selections that were already booked.
 * Then the outstanding amount is added on, to account for unpaid reservations.
 * Negative numbers are clipped to zero, and will be settled as credit by SM.
 * @returns {number}
 */
export function getClubBookingBasketPrice(state, clubBookingID) {
  const { outstandingAmount } = getClubBookingByID(state, clubBookingID);
  const totalPrice = getClubBookingTotalPrice(state, clubBookingID);
  const bookedPrice = WEEKDAYS
    .map((_, weekdayIndex) => getClubBookingBookedSessionID(state, clubBookingID, weekdayIndex))
    .filter(sessionID => sessionID !== null)
    .map(sessionID => getClubBookingSession(state, clubBookingID, sessionID))
    .map(session => session.get("price"))
    .reduce((totalPrice, sessionPrice) => exactMath.add(totalPrice, sessionPrice), 0);
  const basketPrice = exactMath.add(exactMath.sub(totalPrice, bookedPrice), outstandingAmount);
  return Math.max(0, basketPrice);
}

/**
 * @returns {boolean}
 */
export function hasClubBookingBeenModified(state, clubBookingID) {
  return WEEKDAYS.some((_, weekdayIndex) => {
    const bookedSessionID = getClubBookingBookedSessionID(state, clubBookingID, weekdayIndex);
    const selectedSessionID = getClubBookingSelectedSessionID(state, clubBookingID, weekdayIndex);
    return bookedSessionID !== selectedSessionID;
  })
}

/**
 * @returns {boolean}
 */
export function isClubBookingInBasket(state, clubBookingID) {
  return doesBasketItemExist(state, "clubBooking", clubBookingID);
}

/**
 * @returns {boolean}
 */
export function isClubBookingDayHoliday(state, clubBookingID, weekdayIndex) {
  const clubBooking = getClubBookingByID(state, clubBookingID);
  return clubBooking.getIn(["holidays", weekdayIndex]);
}

/**
 * @returns {boolean}
 */
export function doesClubBookingHaveLimitedPlaces(state, clubBookingID) {
  const clubBooking = getClubBookingByID(state, clubBookingID);
  return clubBooking.get("hasLimitedPlaces");
}

/**
 * @returns {boolean}
 */
export function isClubBookingDayFull(state, clubBookingID, weekdayIndex) {
  if (!doesClubBookingHaveLimitedPlaces(state, clubBookingID)) {
    return false;
  }
  const clubBooking = getClubBookingByID(state, clubBookingID);
  return clubBooking.getIn(["placesLimitReached", weekdayIndex]);
}

/**
 * @returns {boolean}
 */
export function isClubBookingExpanded(state, clubBookingID) {
  return state.getIn(["school_money", "expandedClubBookingID"]) === clubBookingID;
}

/**
 * @returns {boolean}
 */
export function isClubBookingSeen(state, clubBookingID) {
  return state.getIn(["school_money", "seenClubBookingIDs"]).includes(clubBookingID);
}

/**
 * @returns {number}
 */
export function getNewClubBookingCount(state) {
  const clubBookingIDList = getClubBookingIDList(state);
  return clubBookingIDList
    .filterNot(clubBookingID => isClubBookingSeen(state, clubBookingID))
    .count();
}

/**
 * @returns {boolean}
 */
export function isClubsReady(state) {
  return !state.getIn(["school_money", "isActivePaymentsLoading"]);
}

/**
 * @returns {string}
 */
export function getGoToClubBookingID(state) {
  return state.getIn(["school_money", "goToClubBookingID"]);
}

/**
 * @returns {date}
 */
export function getClubBookingDayDate(state, clubBookingID, weekdayIndex) {
  const clubBooking = getClubBookingByID(state, clubBookingID);
  return clubBooking.dates.get(weekdayIndex);
}

/**
 * @returns {boolean}
 */
export function isClubBookingDayExpired(state, clubBookingID, weekdayIndex) {
  const clubBooking = getClubBookingByID(state, clubBookingID);
  return clubBooking.expired.get(weekdayIndex);
}

export function getClubBookingLimitedPlacesDetails(state, clubBookingID) {
  const clubBooking = getClubBookingByID(state, clubBookingID);
  return {
    enabled: clubBooking.hasLimitedPlaces,
    total: clubBooking.dailyPlacesLimit,
  }
}

/**
 * @returns {boolean}
 */
export function doesClubBookingHaveBookings(state, clubBookingID) {
  const clubBooking = getClubBookingByID(state, clubBookingID);
  return clubBooking.bookedSessionIDs.some(sessionID => sessionID !== null);
}

/**
 * @returns {boolean}
 */
export function isClubBookingReserved(state, clubBookingID) {
  const isBooked = doesClubBookingHaveBookings(state, clubBookingID);
  const { outstandingAmount } = getClubBookingByID(state, clubBookingID);
  return isBooked && outstandingAmount > 0;
}


/**
  * @returns {boolean}
*/
export function forceClubBookingForStudent(state, studentID){
  return state.getIn(["school_money", "clubBookings"])
    .filter(clubBooking => clubBooking.forceSessionBooking === true)
    .filter(clubBooking => clubBooking.studentID === studentID)
    .keySeq().toList().size > 0;
}



// WEBPACK FOOTER //
// ./src/state/school_money/selectors/clubs.js